import { loginUser, registerUser, logoutUser, verifyEmail, resendCode, requestPasswordReset, confirmPasswordReset } from '../services/api';
import { sanitizeForm, validators, checkRateLimit, secureStorage, safeLog, checkTaxDocument } from '../utils/security';

export const handleLogin = async (rawForm, login, navigate) => {
  if (!checkRateLimit('login', 5, 60000))
    return { error: 'Çok fazla giriş denemesi. Lütfen 1 dakika bekleyin.' };

  const form = sanitizeForm(rawForm, { email: 254, password: 128 });

  if (!validators.email(form.email))
    return { error: 'Geçerli bir e-posta adresi girin.' };
  if (!form.password || form.password.length < 6)
    return { error: 'Şifre en az 6 karakter olmalıdır.' };

  try {
    const res  = await loginUser({ email: form.email, password: form.password });
    const { token, refresh, user } = res.data;

    secureStorage.setToken(token);
    if (refresh) sessionStorage.setItem('_r', refresh);

    safeLog('login:success', { email: form.email });
    login(user);

    // Route to correct dashboard based on role
    if (user.role === 'admin')    navigate('/dashboard/admin');
    else if (user.role === 'supplier') navigate('/dashboard/supplier');
    else navigate('/dashboard/customer');

    return { success: true };
  } catch (err) {
    const status = err.response?.status;
    if (status === 403) {
      const data = err.response?.data || {};
      // Unverified account: signal the UI to show the code-entry step.
      if (data.needs_verification) {
        return { needsVerification: true, user_id: data.user_id, error: data.message };
      }
      return { error: data.message || 'Hesabınız doğrulanmamış.' };
    }
    return { error: 'E-posta veya şifre hatalı.' };
  }
};

export const handleRegisterCustomer = async (rawForm, captchaToken = '') => {
  if (!checkRateLimit('register', 3, 60000))
    return { error: 'Çok fazla kayıt denemesi. Lütfen bekleyin.' };

  const form = sanitizeForm(rawForm, {
    email: 254, full_name: 100, phone: 15,
    password: 128, confirm_password: 128,
  });

  if (!validators.name(form.full_name))   return { error: 'Ad soyad en az 2 karakter olmalıdır.' };
  if (!validators.phone(form.phone))      return { error: 'Geçerli bir telefon numarası girin.' };
  if (!validators.email(form.email))      return { error: 'Geçerli bir e-posta adresi girin.' };
  const pw = validators.password(form.password);
  if (!pw.valid)                          return { error: pw.message };
  if (form.password !== form.confirm_password) return { error: 'Şifreler eşleşmiyor.' };
  if (!form.terms)                        return { error: 'Kullanım koşullarını kabul etmelisiniz.' };

  try {
    const res = await registerUser({ ...form, role: 'customer', captcha_token: captchaToken });
    return { success: true, user_id: res.data.user_id, message: res.data.message };
  } catch (err) {
    const errors = err.response?.data?.errors;
    if (errors) {
      const first = Object.values(errors)[0];
      return { error: Array.isArray(first) ? first[0] : first };
    }
    return { error: 'Kayıt başarısız. Lütfen tekrar deneyin.' };
  }
};

export const handleRegisterSupplier = async (rawForm, captchaToken = '', taxDocument = null) => {
  const form = sanitizeForm(rawForm, {
    email: 254, firm_name: 150, tax_no: 11,
    city: 80, phone: 15, category: 100,
    password: 128, confirm_password: 128,
  });

  if (!form.firm_name || form.firm_name.length < 2) return { error: 'Firma adı en az 2 karakter olmalıdır.' };
  if (!validators.taxNo(form.tax_no))
    return { error: 'Geçerli bir vergi numarası (10 hane) veya T.C. kimlik numarası (11 hane) girin.' };
  const docError = checkTaxDocument(taxDocument);
  if (docError)                                      return { error: docError };
  if (!form.city)                                    return { error: 'Şehir seçimi zorunludur.' };
  if (!validators.phone(form.phone))                 return { error: 'Geçerli bir telefon numarası girin.' };
  if (!validators.email(form.email))                 return { error: 'Geçerli bir e-posta adresi girin.' };
  const pw = validators.password(form.password);
  if (!pw.valid)                                     return { error: pw.message };
  if (form.password !== form.confirm_password)       return { error: 'Şifreler eşleşmiyor.' };
  if (!form.terms)                                   return { error: 'Kullanım koşullarını kabul etmelisiniz.' };
  // Count an attempt only once the form is valid, so fixing typos doesn't lock the user out.
  if (!checkRateLimit('register', 3, 60000))
    return { error: 'Çok fazla kayıt denemesi. Lütfen bekleyin.' };

  const fd = new FormData();
  Object.entries({ ...form, captcha_token: captchaToken }).forEach(([k, v]) => fd.append(k, v ?? ''));
  fd.append('tax_certificate', taxDocument);
  fd.append('role', 'supplier');

  try {
    const res = await registerUser(fd);
    return { success: true, user_id: res.data.user_id, message: res.data.message };
  } catch (err) {
    const errors = err.response?.data?.errors;
    if (errors) {
      const first = Object.values(errors)[0];
      return { error: Array.isArray(first) ? first[0] : first };
    }
    return { error: 'Kayıt başarısız. Lütfen tekrar deneyin.' };
  }
};

export const handleVerifyCode = async (userId, code) => {
  const clean = String(code).replace(/\D/g, '').slice(0, 6);
  if (clean.length !== 6) return { error: 'Geçerli bir 6 haneli kod girin.' };

  try {
    const res = await verifyEmail({ user_id: userId, code: clean });
    return { success: true, token: res.data.token, user: res.data.user };
  } catch (err) {
    return { error: err.response?.data?.message || 'Kod hatalı.' };
  }
};

export const handleResendCode = async (userId) => {
  try {
    await resendCode({ user_id: userId });
    return { success: true };
  } catch (err) {
    return { error: err.response?.data?.message || 'Kod gönderilemedi.' };
  }
};

export const handlePasswordResetRequest = async (email) => {
  if (!validators.email(email)) return { error: 'Geçerli bir e-posta adresi girin.' };
  try {
    const res = await requestPasswordReset({ email });
    return { success: true, message: res.data.message };
  } catch (err) {
    return { error: err.response?.data?.message || 'İstek gönderilemedi. Lütfen tekrar deneyin.' };
  }
};

export const handlePasswordResetConfirm = async (email, code, newPassword) => {
  const clean = String(code).replace(/\D/g, '').slice(0, 6);
  if (clean.length !== 6) return { error: 'Geçerli bir 6 haneli kod girin.' };
  const pw = validators.password(newPassword);
  if (!pw.valid) return { error: pw.message };
  try {
    const res = await confirmPasswordReset({ email, code: clean, new_password: newPassword });
    return { success: true, message: res.data.message };
  } catch (err) {
    return { error: err.response?.data?.message || 'Şifre sıfırlanamadı.' };
  }
};

export const handleLogout = async (logout, navigate) => {
  try {
    const refresh = sessionStorage.getItem('_r');
    if (refresh) await logoutUser({ refresh });
  } catch { /* ignore */ }
  secureStorage.clearToken();
  sessionStorage.removeItem('_r');
  logout();
  // Hard redirect to the homepage. A SPA navigate() would briefly leave us on
  // the /dashboard URL with user=null, so PrivateRoute would bounce to /login.
  // replace() also keeps the back button from returning to the dashboard.
  window.location.replace('/');
};